<template>
  <div>
    <!-- ========================================================= -->
    <!-- 1. FULL PROFILE VIEW (Purple/White Card)                  -->
    <!-- ========================================================= -->
    <div v-if="visibilityOption === 'resumeView'" class="flex justify-center w-full">
      <div class="purple-card-container shadow-lg">
        
        <!-- Custom Close Icon -->
        <div class="card-close-icon shadow-md" @click="closeModal">
          <feather-icon icon="XIcon" class="w-5 h-5 cursor-pointer" />
        </div>

        <!-- Header Section -->
        <div class="card-header">
          <div class="avatar-wrapper">
             <vs-avatar 
               v-if="userData.profilePictureUrl"
               size="100px"
               class="tc-avatar-component"
               :src="constants.s3Bucket + '/editor/profile-pictures/' + userData.profilePictureUrl" 
             />
             <vs-avatar 
               v-else 
               size="100px"
               class="tc-avatar-component"
               :src="`https://hostedboringavatars.vercel.app/api/beam?name=${avatarNameGenerator(userData.firstName || 'A', userData.lastName || 'B')}&colors=F2C244,E77E23,7367F0,28C76F,EA5455`" 
             />
          </div>
        </div>

        <!-- Card Body -->
        <div class="card-body">
          <h2 class="user-name">{{ userData.firstName | title }} {{ userData.lastName | title }}</h2>
          <p class="user-role">{{ (boardUserData && boardUserData.roleTitle && boardUserData.roleTitle.label) || (boardUserData && boardUserData.roleTitle) || 'Candidate' }}</p>

          <div class="divider"></div>

          <!-- CAREER OBJECTIVE -->
          <div class="info-section" v-if="userData.careerObjective">
            <h5 class="section-label">Career Objective</h5>
            <p class="objective-text">
              {{ userData.careerObjective }}
            </p>
          </div>

          <!-- ABOUT (Fallback if no objective) -->
          <div class="info-section" v-else-if="userData.about">
            <h5 class="section-label">About</h5>
            <p class="objective-text">
              {{ userData.about }}
            </p>
          </div>

          <!-- CONTACT INFO -->
          <div class="info-section contact-info">
            <h5 class="section-label">Contact Info</h5>

            <div class="contact-row" v-if="userData.address || userData.currentLocation">
              <feather-icon icon="MapPinIcon" class="icon-small" />
              <span>{{ userData.address || userData.currentLocation }}</span>
            </div>

            <!-- PHONE & WHATSAPP / TELEGRAM ICONS -->
            <div class="contact-row" v-if="userData.contactNumber">
              <feather-icon icon="PhoneIcon" class="icon-small" />
              <span style="margin-right: 12px;">{{ userData.contactNumber }}</span>
              
              <div class="social-actions">
                <a :href="getWhatsAppLink(userData.contactNumber)" target="_blank" title="Message on WhatsApp" class="social-icon whatsapp">
                  <svg style="width:18px;height:18px;" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                  </svg>
                </a>
                <a :href="getTelegramLink(userData.contactNumber)" target="_blank" title="Message on Telegram" class="social-icon telegram">
                  <svg style="width:18px;height:18px;" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.504 8.527l-1.854 8.74c-.139.63-.513.784-1.04.488l-2.875-2.119-1.388 1.336c-.153.153-.282.282-.578.282l.207-2.913 5.305-4.791c.231-.205-.05-.319-.358-.113l-6.557 4.126-2.825-.883c-.614-.192-.627-.614.128-.909l11.04-4.254c.511-.192.964.116.804.91z"/>
                  </svg>
                </a>
              </div>
            </div>

            <!-- EMAIL LINK -->
            <div class="contact-row" v-if="userData.email">
              <feather-icon icon="MailIcon" class="icon-small" />
              <a :href="'mailto:' + userData.email" class="email-link" title="Send Email">
                {{ userData.email }}
              </a>
            </div>
          </div>

          <!-- SKILLS -->
          <div class="info-section" v-if="formattedSkills.length > 0">
            <h5 class="section-label">Skills</h5>
            <div class="skills-container">
              <span v-for="(skill, i) in formattedSkills" :key="i" class="skill-chip">
                {{ skill }}
              </span>
            </div>
          </div>
          
          <!-- ATTACHMENTS SECTION -->
          <div class="info-section" v-if="hasAttachments">
            <h5 class="section-label">Attachments</h5>
            <div v-for="(file, idx) in availableAttachments" :key="idx" class="contact-row mb-1">
              <feather-icon icon="PaperclipIcon" class="icon-small" />
              <a :href="file.url || file" target="_blank" class="email-link">
                {{ file.name || 'View Attached Document' }}
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>


    <!-- ========================================================= -->
    <!-- 2. PROFILE CARD ONLY VIEW (Yellow Card)                   -->
    <!-- ========================================================= -->
    <div v-else class="flex justify-center w-full">
      <div class="strict-profile-card bg-primary relative shadow-lg">
        
        <!-- Custom Close Icon -->
        <div class="main-card-close shadow-md" @click="closeModal">
          <feather-icon icon="XIcon" class="w-5 h-5" />
        </div>

        <!-- Top Section: Avatar & Info -->
        <div class="flex flex-col items-center text-center mt-2">
          <div style="position: relative; margin-bottom: 1rem;">
            <vs-avatar
              v-if="userData.profilePictureUrl"
              class="profile-picture-yellow m-0"
              size="110px"
              :src="constants.s3Bucket + '/editor/profile-pictures/' + userData.profilePictureUrl"
            />
            <vs-avatar
              v-else
              class="profile-picture-yellow m-0"
              size="110px"
              :src="`https://hostedboringavatars.vercel.app/api/beam?name=${avatarNameGenerator(userData.firstName || 'A', userData.lastName || 'B')}`"
            />
          </div>
          
          <div class="mb-base w-full">
            <div class="name-yellow text-white mb-1">
              {{ userData.firstName | title }}
              {{ userData.lastName | title }} 
            </div>
            <div v-if="userData.email" class="text-white text-sm mb-3 lowercase">
              {{ userData.email }}
            </div>
            <div v-if="boardUserData && boardUserData.roleTitle" class="role-title-yellow my-2 text-white font-medium">
              {{ (boardUserData.roleTitle && boardUserData.roleTitle.label) || boardUserData.roleTitle }}
            </div>
            
            <!-- SOCIAL & ACTION ICONS -->
            <vs-row class="items-center mt-4" style="justify-content: center">
              <div v-if="userData.linkedinProfile" class="flex mx-1 items-center">
                <vs-button radius color="white" text-color="grey" type="filled" icon-pack="feather" icon="icon-linkedin" :href="userData.linkedinProfile" target="_blank" style="width: 35px; height: 35px;"></vs-button>
              </div>
              <div v-if="userData.facebookProfile" class="flex mx-1 items-center">
                <vs-button radius color="white" text-color="grey" type="filled" icon-pack="feather" icon="icon-facebook" :href="userData.facebookProfile" target="_blank" style="width: 35px; height: 35px;"></vs-button>
              </div>
              <div v-if="userData.twitterProfile" class="flex mx-1 items-center">
                <vs-button radius color="white" text-color="grey" type="filled" icon-pack="feather" icon="icon-twitter" :href="userData.twitterProfile" target="_blank" style="width: 35px; height: 35px;"></vs-button>
              </div>
              <div v-if="userData.contactNumber" class="flex mx-1 items-center">
                <vs-button radius color="white" text-color="grey" type="filled" icon-pack="feather" icon="icon-phone" :href="`https://wa.me/${userData.contactNumber}`" target="_blank" style="width: 35px; height: 35px;"></vs-button>
              </div>
              <div v-if="userData.email" class="flex mx-1 items-center">
                <a :href="`mailto:${userData.email}`" target="_blank">
                  <vs-button radius color="white" text-color="grey" type="filled" icon="mail" style="width: 35px; height: 35px;"></vs-button>
                </a>
              </div>
            </vs-row>
          </div>
        </div>

        <div v-if="userData.about || hasAttachments" class="mt-5 mb-4">
          <hr style="border-top: 1px solid rgba(255,255,255,0.3); border-bottom: none;" />
        </div>

        <!-- ABOUT SECTION -->
        <div class="text-left" v-if="userData.about">
          <h6 class="heading mb-2 text-white font-semibold">About</h6>
          <p class="content-yellow text-white-o8" style="line-height: 1.6;">
            {{ userData.about }}
          </p>
        </div>

        <!-- ATTACHMENTS SECTION -->
        <div class="mt-4 text-left" v-if="hasAttachments">
          <h6 class="heading mb-2 text-white font-semibold">Attachments</h6>
          <div v-for="(file, idx) in availableAttachments" :key="idx" class="flex items-center gap-2 mb-1">
            <feather-icon icon="PaperclipIcon" class="w-4 h-4 text-white" />
            <a :href="file.url || file" target="_blank" class="text-white-o8 underline content-yellow">
              {{ file.name || 'View Attached Document' }}
            </a>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import constants from "@/constants";

export default {
  props: {
    boardUserData: {
      type: Object,
      required: true,
      default: () => ({})
    },
    visibilityOption: {
      type: String,
    },
  },
  data() {
    return {
      constants,
    };
  },
  computed: {
    // THIS IS THE SECRET SAUCE. It safely provides the data, or an empty object if missing!
    userData() {
      return (this.boardUserData && this.boardUserData.userData) || {};
    },
    hasAttachments() {
      return (this.boardUserData && this.boardUserData.attachments && this.boardUserData.attachments.length > 0) || 
             (this.userData.attachments && this.userData.attachments.length > 0);
    },
    availableAttachments() {
      if (this.boardUserData && this.boardUserData.attachments && this.boardUserData.attachments.length > 0) {
        return this.boardUserData.attachments;
      }
      if (this.userData.attachments) {
        return this.userData.attachments;
      }
      return [];
    },
    formattedSkills() {
      if (!this.userData) return [];
      if (this.userData.skills && this.userData.skills.length) {
         return this.userData.skills.map(s => s.title || s);
      }
      if (this.userData.extraCurricular && this.userData.extraCurricular.length > 0) {
        const ec = this.userData.extraCurricular[0];
        if (ec.skills && Array.isArray(ec.skills)) {
          return ec.skills.map(s => s.title || s);
        }
      }
      return [];
    },
    avatarNameGenerator() {
      return this.$store.state.users.avatarNameGenerator;
    },
  },
  mounted() {
    // Strip the ugly white borders!
    this.stripWhiteParentPopup();
    setTimeout(this.stripWhiteParentPopup, 100);
    setTimeout(this.stripWhiteParentPopup, 300);
  },
  updated() {
    this.stripWhiteParentPopup();
  },
  methods: {
    getWhatsAppLink(phone) {
      if (!phone) return '#';
      const cleaned = phone.toString().replace(/\D/g, '');
      return `https://wa.me/${cleaned}`;
    },
    getTelegramLink(phone) {
      if (!phone) return '#';
      const cleaned = phone.toString().replace(/[\s\-\(\)]/g, '');
      const formatted = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
      return `https://t.me/${formatted}`;
    },
    stripWhiteParentPopup() {
      if (!this.$el) return;
      let parent = this.$el.parentNode;
      while (parent && parent.classList) {
        if (parent.classList.contains('vs-popup')) {
          parent.classList.add('transparent-talent-modal');
          break;
        }
        parent = parent.parentNode;
      }
    },
    closeModal() {
      this.$emit('close'); 
      let parent = this.$parent;
      while (parent) {
        if (parent.$options._componentTag === 'vs-popup' || (parent.$el && parent.$el.classList && parent.$el.classList.contains('vs-popup'))) {
          parent.$emit('update:active', false);
          return; 
        }
        parent = parent.$parent;
      }
      // Backup DOM click
      const closeBtn = document.querySelector('.transparent-talent-modal .vs-popup--close');
      if (closeBtn) closeBtn.click();
    }
  },
};
</script>

<style scoped>
/* ================================================= */
/* 1. PURPLE CARD (Full Profile View) STYLES       */
/* ================================================= */
.purple-card-container {
  width: 400px;
  max-width: 95vw;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.card-close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 100;
  color: #626262;
  background: rgba(255,255,255,0.9);
  border-radius: 50%;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.card-close-icon:hover {
  background: white;
  transform: scale(1.05);
}

.card-header {
  height: 100px;
  background: #fdfdfd;
  display: flex;
  justify-content: center;
  position: relative;
}

.avatar-wrapper {
  position: absolute;
  bottom: -50px;
  z-index: 10;
}

.tc-avatar-component {
  border: 5px solid white !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.card-body {
  background: linear-gradient(135deg, #7367F0 0%, #9E95F5 100%);
  color: white;
  padding: 65px 30px 40px 30px;
  text-align: left;
}

.user-name {
  text-align: center;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.user-role {
  text-align: center;
  font-size: 1rem;
  opacity: 0.9;
  margin-top: 4px;
  margin-bottom: 25px;
}

.divider {
  height: 1px;
  background: rgba(255,255,255,0.25);
  margin-bottom: 25px;
}

.info-section {
  margin-bottom: 25px;
}

.section-label {
  font-size: 0.75rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.7);
  font-weight: 600;
  margin-bottom: 10px;
}

.objective-text {
  font-size: 0.9rem;
  line-height: 1.6;
}

.contact-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.social-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.social-icon {
  color: white;
  transition: all 0.2s ease;
  cursor: pointer;
}

.social-icon:hover {
  transform: scale(1.15);
}

.social-icon.whatsapp:hover { color: #25D366; }
.social-icon.telegram:hover { color: #229ED9; }

.email-link {
  color: white;
  text-decoration: none;
  transition: opacity 0.2s;
}

.email-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.icon-small {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  opacity: 0.8;
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-chip {
  background: rgba(255,255,255,0.25);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
}

/* ================================================= */
/* 2. YELLOW CARD (Profile Card Only View) STYLES  */
/* ================================================= */
.strict-profile-card {
  width: 420px !important;
  max-width: 95vw;           
  margin: 0 auto;             
  border-radius: 16px;        
  padding: 2.5rem 2rem;       
  box-sizing: border-box;
  position: relative; 
}

.main-card-close {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 100;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.1); 
  border-radius: 50%;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.main-card-close:hover {
  background: rgba(0, 0, 0, 0.25);
  color: #ffffff;
  transform: scale(1.05);
}

.text-white-o8 { color: rgba(255, 255, 255, 0.9) !important; }

.profile-picture-yellow {
  border: 4px solid #ffffff !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.name-yellow {
  font-weight: 700;
  font-size: 24px;
  line-height: 1.2;
}

.role-title-yellow {
  font-size: 14px;
  opacity: 0.95;
}

.content-yellow {
  font-size: 13px;
}

.con-vs-avatar >>> img {
  object-fit: cover;
}
</style>

<!-- GLOBAL CSS DESTROYS THE WHITE BOX IF OUR COMPONENT IS INSIDE IT -->
<style>
.transparent-talent-modal {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.transparent-talent-modal .vs-popup--header {
  display: none !important; 
}

.transparent-talent-modal .vs-popup--content {
  padding: 0 !important; 
  margin: 0 !important;
  background: transparent !important;
}
</style>